import "./bootstrap";
import Swal from "sweetalert2";
import { customSwal } from "./swal";
import { fileSizeHumanReadable, formatMinutesToHumanReadable, formatRupiah } from "./helpers";
import dayjs from "dayjs";
import {
  formatNumeral,
  registerCursorTracker,
  formatTime,
} from "cleave-zen";

window.formatRupiah = formatRupiah;
window.customSwal = customSwal;
window.Swal = Swal;
window.dayjs = dayjs;
window.formatNumeral = formatNumeral;
window.registerCursorTracker = registerCursorTracker;
window.formatTime = formatTime;

// Bind jQuery from global scope — required for Vite ESM production bundles.
// jQuery is loaded via <script> tag in layout, not bundled by Vite.
// ES modules don't automatically inherit window.$ so we bind it explicitly.
const $ = window.$ || window.jQuery;

function parseRupiahInput(value) {
    if (!value) return '';
    return String(value).replace(/\./g, '').replace(/,/g, '.');
}
window.parseRupiahInput = parseRupiahInput;

/**
 * Extract raw integer digits from a value that may be:
 *   - a PHP float string from JSON  e.g. "5000000.00"  →  "5000000"
 *   - an already-formatted string  e.g. "5.000.000"   →  "5000000"
 *   - a formatted string with decimal  "5.000.000,00" →  "5000000"
 *   - a plain integer string          "5000000"       →  "5000000"
 */
function rawInteger(val) {
    const s = String(val);
    // Plain number (integer or PHP float) — no thousand-separator dots yet
    if (/^\d+(\.\d+)?$/.test(s)) {
        return Math.round(parseFloat(s)).toString();
    }
    // Already formatted — strip comma-decimal, then dot thousand-separators
    return s.replace(/,\d*$/, '').replace(/\./g, '').replace(/[^\d]/g, '');
}

window.initRupiahMask = function(input) {
    if (!input) return;
    if (input.dataset.numeralInit) return; // idempotent – never double-attach
    input.dataset.numeralInit = '1';
    const opts = { numeralThousandsGroupStyle: 'thousand', delimiter: '.', numeralDecimalMark: ',', numeralDecimalScale: 0 };
    if (input.value) {
        const raw = rawInteger(input.value);
        input.value = raw ? formatNumeral(raw, opts) : '';
    }
    registerCursorTracker({ input: input, delimiter: '.' });
    input.addEventListener('input', function(e) {
        // During user input the value is always already-formatted, so simply strip delimiter dots
        const raw = e.target.value.replace(/\./g, '').replace(/[^\d]/g, '');
        input.value = raw ? formatNumeral(raw, opts) : '';
    });
};

/**
 * Set a formatted numeral value on a masked input and trigger the formatter.
 * Use this instead of `input.value = rawNumber` or `$(input).val(rawNumber)`
 * so the thousand-separator display is applied immediately.
 *
 * @param {HTMLInputElement} input
 * @param {string|number} rawValue  Plain number, e.g. 300000 or "300000"
 */
window.numeralValue = function(input, rawValue) {
    if (!input) return;
    // Ensure mask is attached even if called before MutationObserver fires
    if (!input.dataset.numeralInit) {
        if (input.type === 'number') {
            input.setAttribute('type', 'text');
            input.setAttribute('inputmode', 'numeric');
        }
        window.initRupiahMask(input);
    }
    const raw = String(rawValue ?? '').replace(/\./g, '').replace(/,/g, '').replace(/[^\d]/g, '');
    input.value = raw;
    input.dispatchEvent(new Event('input', { bubbles: true }));
};

// ─────────────────────────────────────────────────────────────────────────────
// Global numeral-input auto-initialiser
//
// Applies initRupiahMask to:
//   • input[type="number"]  → converted to type="text" + inputmode="numeric"
//   • input.rupiah-mask     → already type="text", just ensure mask is active
//
// Auto-excluded (formatting NOT applied):
//   • data-no-numeral attribute present
//   • step="any"  (coordinates, scientific measurements)
//   • step < 1    (decimal steps: 0.01, 0.5, 0.001 …)
// ─────────────────────────────────────────────────────────────────────────────
function _shouldSkipNumeral(input) {
    if ('noNumeral' in input.dataset) return true;
    // Never format inputs inside a flatpickr calendar (year, hour, minute, etc.)
    if (input.closest && input.closest('.flatpickr-calendar')) return true;
    const step = input.getAttribute('step');
    if (step === 'any') return true;
    if (step !== null && step !== '' && parseFloat(step) < 1) return true;
    return false;
}

function _applyNumeralMask(input) {
    if (_shouldSkipNumeral(input)) return;
    if (input.type === 'number') {
        input.setAttribute('type', 'text');
        input.setAttribute('inputmode', 'numeric');
    }
    window.initRupiahMask(input); // initRupiahMask owns the data-numeral-init guard
}

window.initAllNumeralInputs = function(root) {
    (root || document).querySelectorAll(
        'input[type="number"]:not([data-numeral-init]), input.rupiah-mask:not([data-numeral-init])'
    ).forEach(_applyNumeralMask);
};

// Strip thousand-separator dots before form submission so the server receives
// raw integers. Re-apply the formatted value after one tick for AJAX forms
// that stay on the same page.
document.addEventListener('submit', function(e) {
    var restore = [];
    e.target.querySelectorAll('input[data-numeral-init]').forEach(function(input) {
        var fmt = input.value;
        input.value = fmt.replace(/\./g, '').replace(/,/g, '.');
        restore.push([input, fmt]);
    });
    requestAnimationFrame(function() {
        restore.forEach(function(pair) {
            if (document.body.contains(pair[0])) {
                pair[0].value = pair[1];
            }
        });
    });
}, true); // capture phase — fires before any jQuery submit handler

// Auto-initialise on DOM ready
document.addEventListener('DOMContentLoaded', function() {
    window.initAllNumeralInputs(document);
});

// Watch for inputs injected dynamically (repeaters, AJAX-loaded rows, modals).
// Only process inputs that are genuinely numeric (type="number" or .rupiah-mask).
// Do NOT apply to plain type="text" inputs — this would corrupt flatpickr,
// date/time, and any other text-based inputs injected into the DOM.
new MutationObserver(function(mutations) {
    mutations.forEach(function(m) {
        m.addedNodes.forEach(function(node) {
            if (node.nodeType !== 1) return;
            if (node.tagName === 'INPUT') {
                if (node.type === 'number' || node.classList.contains('rupiah-mask')) {
                    _applyNumeralMask(node);
                }
            } else {
                window.initAllNumeralInputs(node);
            }
        });
    });
}).observe(document.documentElement, { childList: true, subtree: true });

/**
 * Show a toast notification using SweetAlert2.
 *
 * @param {'success'|'error'|'warning'|'info'} type
 * @param {string} message
 * @param {number} timer  Auto-close delay in ms (default 3000)
 */
function showToast(type = 'success', message = '', timer = 3000) {
    const Toast = Swal.mixin({
        toast: true,
        position: 'top-end',
        showConfirmButton: false,
        timer,
        timerProgressBar: true,
        didOpen: (toast) => {
            toast.addEventListener('mouseenter', Swal.stopTimer);
            toast.addEventListener('mouseleave', Swal.resumeTimer);
        },
    });

    Toast.fire({ icon: type, html: `<span style="font-size:0.82rem">${message}</span>` });
}

window.showToast = showToast;

async function appendBusinessPartnerSelection(
    role = 'customer',
    dataId = null,
    nodeName = ".business_partners"
) {
    const data = await fetchCollections({ model: 'BusinessPartner', query: `role=${role}&is_active=true` });
    var nodes = `<option value="" selected disabled>Choose ${role}</option>`;

    for (const item of data) {
        nodes += `<option ${dataId == item.id ? "selected" : ""} 
            data-address="${item.address}" data-phone="${item.phone}" value="${
            item.id
        }">${item.name}</option>`;
    }

    $(nodeName).html(nodes);
}

async function appendItemSelection({ dataId = null, nodeName = ".items", collections, relations, query, multiUom = false }) {
    const data = collections || await fetchCollections({ model: 'Item', relations: relations || 'uom', query });
    const filteredData = multiUom ? data.filter(item => item.uom_conversions && item.uom_conversions.length > 0) : data;
    var itemsNode = '<option value=""></option>';

    for (const item of filteredData) {
        itemsNode += `<option ${dataId == item.id ? "selected" : ""} value="${
            item.id
        }" data-unit-id="${item.uom.id}" data-unit="${item.uom.name}">
            ${item.name}
        </option>`;
    }

    $(nodeName).html(itemsNode);
}

async function appendGaugeSelection(dataId = null, nodeName = ".gauges") {
    const data = await fetchCollections({ model: 'Gauge' });
    var itemsNode = "<option selected disabled>Choose Gauge</option>";

    for (const item of data) {
        itemsNode += `<option ${dataId == item.id ? "selected" : ""} value="${
            item.id
        }">${item.name}</option>`;
    }

    $(nodeName).html(itemsNode);
}

async function appendMachineTypeSelection(dataId = null, nodeName = ".machine_types") {
    const data = await fetchCollections({ model: 'MachineType' });
    var itemsNode = "<option selected disabled>Choose Machine Type</option>";

    for (const item of data) {
        itemsNode += `<option ${dataId == item.id ? "selected" : ""} value="${
            item.id
        }">${item.name}</option>`;
    }

    $(nodeName).html(itemsNode);
}

async function appendCategorySelection(dataId = null, nodeName = ".categories") {
    const data = await fetchCollections({ model: 'Category' });
    var itemsNode = '<option value="" selected disabled>Choose Category</option>';

    for (const item of data) {
        itemsNode += `<option ${dataId == item.id ? "selected" : ""} value="${
            item.id
        }">${item.name}</option>`;
    }

    $(nodeName).html(itemsNode);
}

async function appendAttributeOptionSelection(dataId = null, nodeName = ".attributes") {
    const data = await fetchCollections({ model: 'AttributeOption', query: 'visible=true' });
    var itemsNode = '<option value="" selected disabled>Choose Attribute</option>';

    for (const item of data) {
        itemsNode += `<option ${dataId == item.name ? "selected" : ""} data-value="${item.value}" value="${
            item.name
        }">${item.name}</option>`;
    }

    $(nodeName).html(itemsNode);
}

async function appendMachineSelection(dataId = null, nodeName = ".machines", customQuery = '') {
    const data = await fetchCollections({ model: 'Machine', relations: 'machineType', query: customQuery });
    var itemsNode = '<option value="" selected disabled>Choose Machine</option>';

    for (const item of data) {
        itemsNode += `<option ${dataId == item.id ? "selected" : ""} data-capacity="${item.input_capacity}" value="${
            item.id
        }">${item.name}</option>`;
    }

    $(nodeName).html(itemsNode);
}

async function appendTransactionSelection(model = 'Purchase', dataId = null, nodeName = ".transactions") {
    const data = await fetchCollections({ model });
    var nodes = `<option selected disabled>Choose ${model}</option>`;

    for (const item of data) {
        nodes += `<option ${dataId == item.id ? "selected" : ""} value="${
            item.id
        }">${item.code} - ${item.title}</option>`;
    }

    $(nodeName).html(nodes);
}

async function appendEmployeeSelection(value = null, nodeName = '.employees', title = 'Employee', collections) {
    const data = collections || await fetchCollections({ model: 'Employee' });
    var nodes = `<option value="" selected disabled>Choose ${title}</option>`;

    for (const item of data) {
        nodes += `<option ${value == item.id ? 'selected' : ''} value="${item.id}">${item.full_name}</option>`;
    }

    $(nodeName).html(nodes);
}

async function appendMachineStatusSelection(value = null, nodeName = ".status") {
    const data = [
        {
            'label': 'Available',
            'value': 'AVAILABLE',
        },
        {
            'label': 'In Use',
            'value': 'IN_USE',
        },
        {
            'label': 'Maintenance',
            'value': 'MAINTENANCE',
        },
        {
            'label': 'Offline',
            'value': 'OFFLINE',
        },
    ];
    var nodes = `<option selected disabled>Choose Status</option>`;

    for (const item of data) {
        nodes += `<option ${value == item.value ? "selected" : ""} value="${
            item.value
        }">${item.label}</option>`;
    }

    $(nodeName).html(nodes);
}

async function generateDeliveryOrderPdf({ id, type }) {
    return window.open(`/generate-pdf/delivery-orders/${id}?type=${type}`, '_blank');
}

async function appendTransactionData(id, model) {
    const relations = 'businessPartner,transactionItems,transactionItems.item,transactionItems.item.uom,transactionItems.inventory';
    const data = await fetchCollection({ id, model, relations });
    var badge = "success";
    if (data.status == "active") {
        badge = "info";
    } else if (data.status == "accepted") {
        badge = "primary";
    } else if (data.status == "returned") {
        badge = "warning";
    } else if (data.status == "canceled") {
        badge = "danger";
    }

    $('#modal-detail').find('.modal-title').text(`Transaction Detail`);
    $("#code").text(data.code);
    $("#title").text(data.title);
    $("#dstatus").html(
        `<span class="badge bg-label-${badge} text-sm p-2">${data.status}</span>`
    );
    $("#dtotal").text(
        "Rp. " +
            Number(data.total).toLocaleString("id-ID", {
                minimumFractionDigits: 0,
            })
    );
    $("#transaction_date").text(data.transaction_date);
    $("#transaction-detail").html("");
    var itemNode = "";

    for (const item of data.transaction_items) {
        var statusBadge = "success";
        if (data.status == "active") {
            statusBadge = "info";
        } else if (data.status == "returned" || data.status == "canceled") {
            statusBadge = "danger";
        }
        itemNode += `
                    <tr>
                        <td>${item.item.name}</td>
                        <td>Rp. ${Number(item.inventory.price).toLocaleString(
                            "id-ID",
                            { minimumFractionDigits: 0 }
                        )}</td>
                        <td>${item.quantity}</td>
                        <td>${item.item.uom?.name || item.item.unit}</td>
                        <td>Rp. ${Number(item.total).toLocaleString("id-ID", {
                            minimumFractionDigits: 0,
                        })}</td>
                        <td><span class="badge bg-label-${statusBadge} text-sm p-2">${
                            data.status
                        }</span></td>
                        <td>${item.transaction_date}</td>
                        <td>${item.canceled_at || ""}</td>
                        <td>${item.canceled_reason || ""}</td>
                        <td>
                            <button class="btn btn-sm btn-warning" onclick="showModalReturn(${item.id})"
                                data-bs-toggle="modal" data-bs-target="#modal-return"><i class="bx bx-undo"></i></button>
                            <button class="btn btn-sm btn-danger" onclick="showModalCancel(${item.id})"
                                data-bs-toggle="modal" data-bs-target="#modal-cancel"><i class="bx bx-x-circle"></i></button>
                        </td>
                    </tr>`;
    }

    itemNode += `
                <tr>
                    <td colspan="9" class="text-end fw-bold">Grand Total</td>
                    <td colspan="1" class="fw-bold">Rp. ${Number(
                        data.total
                    ).toLocaleString("id-ID", {
                        minimumFractionDigits: 0,
                    })}</td>
                </tr>`;
    $("#transaction-detail").append(itemNode);
}

async function appendUnitSelection({ dataId = null, nodeName = '.units', collections }) {
    const data = collections || await fetchCollections({ model: 'Uom' });
    var nodes = '<option selected disabled>Choose Unit</option>';
    for (const item of data) {
        nodes += `<option ${dataId == item.id ? 'selected' : ''} value="${item.id}">${item.name} (${item.code})</option>`;
    }
    $(nodeName).html(nodes)
}

async function appendColorSelection({ dataId = null, nodeName = '.colors', collections, relations }) {
    const data = collections || await fetchCollections({ model: 'Color', relations });
    var nodes = '<option value="" selected disabled>Choose Color</option>';
    for (const item of data) {
        nodes += `<option ${dataId == item.id ? 'selected' : ''} value="${item.id}" data-color="${item.hex_code}">
            (${item.vendor.name}) ${item.name}
        </option>`;
    }
    $(nodeName).html(nodes)
}

async function appendGenderSelection(gender = null, nodeName = '.genders') {
    const response = await fetch('/ajax/genders');
    const json = await response.json();
    var nodes = '<option selected disabled>Choose Gender</option>';
    for (const item of json.data) {
        nodes += `<option ${gender == item.value ? 'selected' : ''} value="${item.value}">${item.name}</option>`;
    }
    $(nodeName).html(nodes)
}

async function appendDepartmentSelection(value = null, nodeName = '.departments') {
    const response = await fetch('/ajax/collections?model=Department');
    const json = await response.json();
    var nodes = '<option selected disabled>Choose Department</option>';
    for (const item of json.data) {
        nodes += `<option ${value == item.id ? 'selected' : ''} value="${item.id}">${item.name}</option>`;
    }
    $(nodeName).html(nodes)
}

async function appendItemTypeSelection({ dataId = null, nodeName = '.item_types' }) {
    const response = await fetch('/ajax/item-types');
    const { data: itemTypes } = await response.json();
    var nodes = '<option value="" selected disabled>Choose Type</option>';
    for (const item of itemTypes) {
        nodes += `<option ${dataId == item.value ? 'selected' : ''} value="${item.value}">${item.name}</option>`;
    }
    $(nodeName).html(nodes)
}

async function appendProductSelection({ dataId = null, nodeName = '.products', collections, query }) {
    const data = collections || await fetchCollections({ model: 'Item', relations: 'uom,uomConversions,uomConversions.toUom', query });
    var nodes = '<option value=""></option>';
    for (const item of data) {
        nodes += `<option ${dataId == item.id ? 'selected' : ''} value="${item.id}">${item.name}</option>`;
    }
    $(nodeName).html(nodes)
}

function appendBusinessRoleSelection(dataId = null, nodeName = '.roles') {
    const data = [
        {
            'label': 'Customer',
            'value': 'customer',
        },
        {
            'label': 'Supplier',
            'value': 'supplier',
        },
        {
            'label': 'Vendor',
            'value': 'vendor',
        },
    ];
    var nodes = '<option selected disabled>Choose Business Role</option>';
    for (const role of data) {
        nodes += `<option ${dataId == role.value ? 'selected' : ''} value="${role.value}">${role.label}</option>`;
    }
    $(nodeName).html(nodes)
}

async function appendDefectTypeSelection({ dataId = null, nodeName = ".defect_types" }) {
    const data = await fetchCollections({ model: 'DefectType' });
    var nodes = '<option value=""></option>';

    for (const item of data) {
        nodes += `<option ${dataId == item.id ? "selected" : ""} value="${
            item.id
        }">${item.name}</option>`;
    }

    $(nodeName).html(nodes);
}

async function appendMovementTypeSelection(dataId = null, nodeName = '.movement_types') {
    const data = await fetchCollections({ model: 'MovementType' });
    var nodes = '<option value=""></option>';

    for (const item of data) {
        nodes += `<option ${dataId == item.id ? 'selected' : ''} value="${item.id}">(${item.direction.toUpperCase()}) ${item.name}</option>`;
    }

    $(nodeName).html(nodes);
}

async function appendModuleSelection({ dataId = null, nodeName = ".modules" }) {
    const data = await fetchCollections({ model: 'Module' });
    var nodes = '<option value=""></option>';

    for (const item of data) {
        nodes += `<option ${dataId == item.id ? "selected" : ""} value="${
            item.id
        }">${item.name}</option>`;
    }

    $(nodeName).html(nodes);
}

async function appendUserSelection({ dataId = null, nodeName = ".users" }) {
    const data = await fetchCollections({ model: 'User' });
    var nodes = '<option value=""></option>';

    for (const item of data) {
        nodes += `<option ${dataId == item.id ? "selected" : ""} value="${
            item.id
        }">${item.username}</option>`;
    }

    $(nodeName).html(nodes);
}

async function appendRoleSelection(roleId = null, nodeName = '.roles') {
    const data = await fetchCollections({ model: 'Role' });
    var nodes = '<option selected disabled>Choose Role</option>';

    for (const item of data) {
        nodes += `<option ${roleId == item.id ? 'selected' : ''} value="${item.id}">${item.name}</option>`;
    }

    $(nodeName).html(nodes)
}

async function createBusinessPartner({ selector = '.business_partners', buttonSelector = '#btn-new-business-partner' }) {
    const Modal = new bootstrap.Modal($('#modal-business-partner')[0]);
    const Form = $('#form-create-business-partner');
    const Select = $(selector);
    const btnNew = $(buttonSelector);
    btnNew.on('click', async function () {
        await appendBusinessRoleSelection(null, '.roles');
        Modal.show();
    });
    Form.on('submit', async function (e) {
        e.preventDefault();
        const serialize = $(this).serializeArray();
        const formData = {};
        serialize.forEach(function (item) {
            if (item.name === '_token') {
                return;
            }
            formData[item.name] = item.value;
        });
        try {
            const { data } = await fetchApi({
                endpoint: '/business-partners/create',
                method: 'POST',
                headers: {
                    'X-CSRF-TOKEN': $('input[name="_token"]').val(),
                    'X-Requested-With': 'XMLHttpRequest',
                    'Content-Type': 'application/json',
                },
                body: {
                    ...formData,
                    is_active: true,
                },
            });

            // Append new option dynamically
            Select.append(`<option selected value="${data.id}">${data.name}</option>`);
            // Reset form and hide modal
            Form.trigger('reset');
            Modal.hide();
        } catch (error) {
            showErrorModal(error.message);
        }
    });
}

function getStockLevel(quantity) {
    let stockLevel = 'In Stock';
    if (quantity <= 100 && quantity > 100) {
        stockLevel = 'medium';
    } else if (quantity <= 100) {
        stockLevel = 'low';
    }

    return stockLevel;
}

function getStockBadgeClass(stockLevel) {
    let stockBadgeClass = 'success';
    let stockText = 'In Stock';
    if (stockLevel === 'medium') {
        stockBadgeClass = 'warning';
        stockText = 'Low Stock';
    } else if (stockLevel === 'low') {
        stockBadgeClass = 'danger';
        stockText = 'Very Low';
    }

    return { stockBadgeClass, stockText };
}

function getQualityControlStatusBadge(status) {
    let statusBadge = `<span class="badge bg-label-secondary">WAITING</span>`;

    if (status === 'CHECKING') {
        statusBadge = `<span class="badge bg-label-blue">CHECKING</span>`;
    } else if (status === 'REWORK') {
        statusBadge = `<span class="badge bg-label-purple">REWORK</span>`;
    } else if (status === 'PASSED') {
        statusBadge = `<span class="badge bg-label-success">PASSED</span>`;
    } else if (status === 'PARTIAL') {
        statusBadge = `<span class="badge bg-label-primary">PARTIAL</span>`;
    } else if (status === 'FAILED') {
        statusBadge = `<span class="badge bg-label-danger">FAILED</span>`;
    }

    return statusBadge;
}

function getProductionStatusBadge(status) {
    let statusBadge = `<span class="badge bg-label-secondary">DRAFT</span>`;
    
    if (status === 'IN_PRODUCTION') {
        statusBadge = `<span class="badge bg-label-info">IN PRODUCTION</span>`;
    } else if (status === 'PAUSED') {
        statusBadge = `<span class="badge bg-label-info">PAUSED</span>`;
    } else if (status === 'QC') {
        statusBadge = `<span class="badge bg-label-primary">QC</span>`;
    } else if (status === 'COMPLETED') {
        statusBadge = `<span class="badge bg-label-success">COMPLETED</span>`;
    }

    return statusBadge;
}

async function browseProducts(keyword, orderItems, type = 'purchase') {
    const modalEl = $('#modal-products')[0];
    const Modal = bootstrap.Modal.getInstance(modalEl) || new bootstrap.Modal(modalEl);
    Modal.show();
    $('#modal-products').find('#productResult').html('<p>Loading...</p>');

    let query = '';

    if (keyword) {
        query = `keyword=${keyword}`;
    }

    const data = await fetchCollections({ model: 'Item', relations: 'inventory,uom,uomConversions,uomConversions.toUom', query, limit: 10 });
    const filteredData = data.filter(item => orderItems.every(oi => oi.item_id !== item.id));
    const items = type === 'purchase' ? filteredData : filteredData.filter(item => item.inventory);
    let productResults = '';

    for (const item of items) {
        const uomConversions = item.uom_conversions.map(uc => ({
            ...uc.to_uom,
            conversion_rate: Number.parseFloat(uc.conversion_rate),
        }));
        
        const stockQuantity = item.inventory?.quantity || 0;
        const stockLevel = getStockLevel(stockQuantity);
        const { stockBadgeClass, stockText } = getStockBadgeClass(stockLevel);

        productResults += `
            <div class="product-result-item" onclick="addProduct({
                    id: ${item.id},
                    code: '${item.code}',
                    name: '${item.name}',
                    quantity: ${1},
                    uomId: ${item.uom?.id},
                    uomName: '${item.uom?.name || item.unit}',
                    uoms: '${encodeURIComponent(JSON.stringify(uomConversions))}',
                    price: ${item.price},
                    stockQuantity: ${stockQuantity}
                })">
                <div class="product-info d-flex align-items-center">
                    <div class="mx-2 px-1 me-3 text-primary d-none d-sm-block">
                        <i class="bx bx-package" style="font-size: 1.5rem;"></i>
                    </div>
                    <div>
                        <h6 class="mb-0 fw-bold text-product-heading">${item.name}</h6>
                        <div class="d-flex align-items-center gap-2 mt-1">
                            <span class="product-sku"><i class="bx bx-barcode-reader me-1"></i>${item.code}</span>
                            <span class="badge bg-label-${stockBadgeClass} product-stock" style="font-size: 0.65rem; padding: 0.2rem 0.5rem;">
                                ${stockText}: ${stockQuantity}
                            </span>
                        </div>
                    </div>
                </div>
                <div class="product-price ms-auto">
                    <div class="price mb-0 fs-6">${formatRupiah(item.price)}</div>
                    <small class="text-muted d-block" style="font-size: 0.7rem;">/ ${item.uom?.code || item.uom?.name || ''}</small>
                </div>
            </div>`;
    }

    const noResultTemplate = `<div class="text-center">
        <img src="/assets/icons/no-data.png" alt="No Results" class="mb-3" style="width: 112px;">
        <p class="text-muted">No products found for "<b>${keyword}</b>"</p>
    </div>`;
    const modalTitle = `Search results for "<b>${keyword}</b>"`;

    if (filteredData.length === 0) {
        productResults = noResultTemplate;
    }
    $('#modal-products').find('.modal-title').html(modalTitle);
    $('#modal-products').find('#productResult').html(productResults);
}

async function createMachineType() {
    const Modal = new bootstrap.Modal($('#modal-new')[0]);
    const Form = $('#form-new');
    const Select = $('.machine_types');
    const btnNewCustomer = $('#btn-new');
    btnNewCustomer.on('click', function () {
        Modal.show();
    });
    Form.on('submit', async function (e) {
        e.preventDefault();
        const formData = new FormData(this);
        try {
            const { data } = await fetchApi({
                endpoint: '/machine-types/create',
                method: 'POST',
                headers: {
                    'X-CSRF-TOKEN': $('input[name="_token"]').val(),
                    'X-Requested-With': 'XMLHttpRequest',
                },
                body: formData,
            });

            // Append new option dynamically
            Select.append(`<option selected value="${data.id}">${data.name}</option>`);
            // Reset form and hide modal
            Form.trigger('reset');
            Modal.hide();
        } catch (error) {
            alert(error.message);
        }
    });
}

async function showModalDeliveryOrder(transactionId, deliveryOrderId, type) {
    const code = await fetchDeliveryOrderCode();
    const isServiceRequest = ['Dyeing', 'Makloon'].includes(type);
    const modelMap = { 'Dyeing': 'DyeRequest', 'Makloon': 'MakloonRequest' };
    const fetchModel = isServiceRequest ? modelMap[type] : type;
    const label = ['Sale', 'Dyeing', 'Makloon'].includes(type) ? 'Customer' : 'Supplier';
    const [reference] = await Promise.all([
        fetchCollection({ id: transactionId, model: fetchModel }),
        appendBusinessPartnerSelection(label.toLowerCase()),
        ...(!isServiceRequest ? [appendTransactionSelection(type)] : []),
        appendEmployeeSelection(),
    ]);

    const referenceCode = isServiceRequest ? reference.request_no : reference.code;
    $('input[name="delivery_order_no"]').val(code);
    const Modal = new bootstrap.Modal($('#modal-delivery-order')[0]);
    const modal = $('#modal-delivery-order');
    modal.find('#modalTitle').text(`#${referenceCode} - Delivery Order`);
    modal.find('input[name="reference_id"]').val(reference.id);
    modal.find('input[name="delivery_type"]').val(type.toLowerCase());
    modal.find('#employeeSenderReceiver').html(['Sale', 'Dyeing', 'Makloon'].includes(type)
        ? '<i class="bx bx-user-check me-1"></i>Sender <span class="text-danger">*</span>'
        : '<i class="bx bx-user-check me-1"></i>Receiver <span class="text-danger">*</span>');

    if (deliveryOrderId) {
        const deliveryOrder = await fetchCollection({ id: deliveryOrderId, model: 'DeliveryOrder', relations: 'documents' });
        const employeeAttribute = ['Sale', 'Dyeing', 'Makloon'].includes(type) ? 'sender_id' : 'receiver_id';
        const buttons = modal.find('#action-button');
        modal.find('form').attr('action', `/delivery-orders/update/${deliveryOrderId}`);
        modal.find('#deliveryOrderStatus').html(`<span class="badge bg-label-${getTransactionStatusBadge(reference.status, deliveryOrder.status)} p-2">${deliveryOrder.status.replace('_', ' ')}</span>`);
        modal.find('input[name="delivery_order_no"]').val(deliveryOrder.delivery_order_no);
        modal.find('input[name="delivery_type"]').val(deliveryOrder.delivery_type);
        modal.find('input[name="delivery_date"]').val(deliveryOrder.delivery_date);
        modal.find('input[name="vehicle_number"]').val(deliveryOrder.vehicle_number);
        modal.find('input[name="driver_name"]').val(deliveryOrder.driver_name);
        modal.find('textarea[name="remarks"]').val(deliveryOrder.remarks);
        modal.find('#comments').val(deliveryOrder.comments);
        await appendEmployeeSelection(deliveryOrder[employeeAttribute], 'select[name="employee_id"]');
        const [modules, approvals, { data: me }] = await Promise.all([
            fetchCollections({ model: 'Module' }),
            fetchCollections({ model: 'ApprovalSetting' }),
            fetchApi({ endpoint: '/ajax/me', method: 'GET', headers: { 'Content-Type': 'application/json' } }),
        ]);
        let contentButtons = `
            <button 
                type="button" 
                onclick="generateDeliveryOrderPdf({ id: '${deliveryOrderId}', type: '${type.toLowerCase()}' })" 
                class="btn btn-outline-primary"><i class="bx bx-printer me-1"></i> Print</button>
        `;
        let approvalSetting = null;

        for (const module of modules) {
            if (module.name === 'Delivery Orders') {
                approvalSetting = approvals.find(app => app.module_id === module.id && app.user_id === me.id);
                break;
            }
        }

        if (approvalSetting) {
            if (['ATTACHED', 'REJECTED'].includes(deliveryOrder.status)) {
                contentButtons += `<button type="button" onclick="updateDeliveryOrderStatus('${deliveryOrderId}', 'reject', '${type}')" class="btn btn-outline-danger">Reject</button>
                <button type="button" onclick="updateDeliveryOrderStatus('${deliveryOrderId}', 'approve', '${type}')" class="btn btn-success">Approve</button>`;
                modal.find('#commentSection').removeClass('d-none');
                modal.find('#comments').attr('disabled', false);
            } else {
                modal.find('#commentSection').removeClass('d-none');
                modal.find('#comments').attr('disabled', false);
                modal.find('#comments').attr('readonly', true);
                modal.find('#deliveryOrderDocumentsForm').addClass('d-none');
                modal.find('#deliveryOrderDocumentsPreview').addClass('d-none');
            }
        } else if (!approvalSetting && ['IN_PROCESS', 'ATTACHED', 'REJECTED'].includes(deliveryOrder.status)) {
            contentButtons += `<button type="submit" class="btn btn-primary">Update</button>`;
            modal.find('#deliveryOrderDocumentsForm').removeClass('d-none');
            initializeDropzoneUpload({
                formId: 'deliveryOrderInformationForm',
                endpoint: `/delivery-orders/update/${deliveryOrderId}`,
                redirectPath: getDeliveryOrderRedirectPath(type),
            });
        } else if (['ARRIVED', 'REJECTED'].includes(deliveryOrder.status)) {
            modal.find('#commentSection').removeClass('d-none');
            modal.find('#comments').attr('disabled', false);
            modal.find('#comments').attr('readonly', true);
            modal.find('#comments').attr('required', false);
        }

        buttons.html(contentButtons);
        if (deliveryOrder.documents.length > 0) {
            modal.find('#deliveryOrderDocumentsPreview').removeClass('d-none');
            modal.find('#deliveryOrderDocumentsPreview').html('<h5 class="modal-title">Attachments Preview</h5>');
        }

        for (const document of deliveryOrder.documents) {
            $('#deliveryOrderDocumentsPreview').append(`
                <div class="col-sm-6 col-md-3 col-12">
                    <div class="card h-100">
                        <div class="d-flex align-items-center justify-content-center file-preview">
                            ${document.extension === 'pdf'
                                ? `<div>No Preview</div>`
                                : `<img src="${document.file_path}" alt="${document.file_name}" class="img-fluid" />`}
                        </div>
                        <div class="card-body p-3 d-flex flex-column justify-content-between">
                            <div class="card-text">${document.file_name}</div>
                            <div class="card-text">
                                <small class="text-body-secondary">${fileSizeHumanReadable(document.file_size)}</small>
                            </div>
                            <a 
                                class="download-wrapper mt-2" 
                                href="javascript:void(0);" 
                                onclick="downloadFileFromURL('${document.file_path}', '${document.file_name}')">Download</a>
                        </div>
                    </div>
                </div>
            `);
        }
    } else {
        modal.find('#deliveryOrderInformationForm').attr('action', '/delivery-orders/create');
        modal.find('#action-button').html(`
            <button type="submit" class="btn btn-primary">Create</button>
        `);
        modal.find('input[name="delivery_date"]').val(dayjs().format('YYYY-MM-DD'));
        modal.find('input[name="vehicle_number"]').val('');
        modal.find('input[name="driver_name"]').val('');
        modal.find('textarea[name="remarks"]').val('');
        modal.find('textarea[name="comments"]').val('');
        modal.find('#commentSection').addClass('d-none');
        await appendEmployeeSelection(null, 'select[name="employee_id"]');
        modal.find('#deliveryOrderDocumentsPreview').addClass('d-none');
        modal.find('#deliveryOrderDocumentsForm').addClass('d-none');
    }

    Modal.show();
}

function downloadFileFromURL(url, fileName) {
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
}

function initializeDropzoneUpload({ formId, endpoint, redirectPath }) {
    if (Dropzone.instances.length > 0) {
        Dropzone.instances.forEach((dz) => dz.destroy());
    }

    var e = `<div class="dz-preview dz-file-preview">
        <div class="dz-details">
            <div class="dz-thumbnail">
                <img data-dz-thumbnail>
                <span class="dz-nopreview">No preview</span>
                <div class="dz-success-mark"></div>
                <div class="dz-error-mark"></div>
                <div class="dz-error-message"><span data-dz-errormessage></span></div>
                <div class="progress">
                <div class="progress-bar progress-bar-primary" role="progressbar" aria-valuemin="0" aria-valuemax="100" data-dz-uploadprogress></div>
                </div>
            </div>
            <div class="dz-filename" data-dz-name></div>
            <div class="dz-size" data-dz-size></div>
        </div>
    </div>`;
    var a = document.querySelector("#dropzone-multi");
    if (a) {
        Dropzone.autoDiscover = false;
        new Dropzone('#dropzone-multi', {
            url: endpoint,
            method: "post",
            paramName: "documents[]",
            acceptedFiles: ".pdf,.png,.jpg,.webp,.jpeg",
            autoprocessQueue: false,
            uploadMultiple: false,
            previewTemplate: e,
            parallelUploads: 5,
            maxFiles: 5,
            maxFilesize: 5,
            addRemoveLinks: !0,
            init: function() {
                var myDropzone = this;
                const form = document.getElementById(formId);
                form.addEventListener('submit', function(e) {
                    e.preventDefault();
                    e.stopPropagation();

                    myDropzone.processQueue();
                    const formData = new FormData();
                    var queuedFiles = myDropzone.getAcceptedFiles();

                    new FormData(form).forEach((v, k) => {
                        formData.append(k, v);
                    });

                    for (var file of queuedFiles) {
                        formData.append(`documents[]`, file);
                    }

                    postConfirm({
                        endpoint,
                        method: 'POST',
                        redirectPath,
                        data: formData,
                    });
                });
            },
        });
    }
}

function getDeliveryOrderRedirectPath(type) {
    const map = { 'Sale': '/sales', 'Purchase': '/purchases', 'Dyeing': '/dye-requests', 'Makloon': '/makloon-requests' };
    return map[type] || '/purchases';
}

async function updateDeliveryOrderStatus(id, status, type) {
    const endpoint = `/delivery-orders/change-status/${id}`;
    const comments = $('#comments').val()?.trim();
    postConfirm({
        endpoint,
        method: 'PATCH',
        headers: {
            'Content-Type': 'application/json',
        },
        redirectPath: getDeliveryOrderRedirectPath(type),
        data: {
            status,
            comments,
        },
    });
}

function showCreatePage(url) {
    location.href = url;
}

function showEditPage(url, id) {
    location.href = `${url}?id=${id}`;
}

// Time Mask Input
const a = document.querySelectorAll(".time-mask");
if (a) {
    for (const input of a) {
        input.addEventListener("input", (e) => {
            input.value = formatTime(e.target.value, { timePattern: ["h", "m"] });
        });
        registerCursorTracker({ input: input, delimiter: ":" });
    }
}

// Numeral Mask Input
const b = document.querySelectorAll(".rupiah-mask")
if (b) {
    const rupiahOpts = { numeralThousandsGroupStyle: "thousand", delimiter: ".", numeralDecimalMark: ",", numeralDecimalScale: 0 };
    for (const input of b) {
        if (input.value) {
            const raw = rawInteger(input.value);
            input.value = raw ? formatNumeral(raw, rupiahOpts) : '';
        }
        registerCursorTracker({ input: input, delimiter: "." });
        input.addEventListener("input", (e) => {
            // During user input the value is always already-formatted, so simply strip delimiter dots
            const raw = e.target.value.replace(/\./g, '').replace(/[^\d]/g, '');
            input.value = raw ? formatNumeral(raw, rupiahOpts) : '';
        });
    }
}

// Strip rupiah formatting before form submission
document.addEventListener('submit', function(e) {
    const form = e.target;
    const rupiahInputs = form.querySelectorAll('.rupiah-mask');
    rupiahInputs.forEach(function(input) {
        input.value = input.value.replace(/\./g, '').replace(/,/g, '.');
    });
}, true);

const c = document.querySelectorAll(".number-range-mask")
if (c) {
    for (const input of c) {
        input.addEventListener("input", (e) => {
            input.value = formatNumeral(e.target.value, {
                blocks: [2, 2],
                delimiter: "-",
            });
        });
        registerCursorTracker({ input: input, delimiter: "-" });
    }
}

var flatpickrDates = document.querySelectorAll(".flatpickr-input");
if (flatpickrDates) {
    for (const input of flatpickrDates) {
        const opts = { monthSelectorType: "static", dateFormat: "Y-m-d", static: true };
        if (input.dataset.minDate) opts.minDate = input.dataset.minDate;
        if (input.hasAttribute("data-default-today")) opts.defaultDate = "today";
        input.flatpickr(opts);
    }
}

function initSelect2(nodeName, dropdownParent = null) {
    var t = $(nodeName);

    if (!t.length) {
        return;
    }

    const option = {
        allowClear: true,
    };

    if (dropdownParent) {
        option.dropdownParent = $(dropdownParent);
    }

    t.each(function () {
        var e = $(this);
        e.select2({
            placeholder: `Choose ${e.data("placeholder") || "Option"}`,
            ...option,
        });
    });
}

function getTransactionStatusBadge(status, $deliveryOrderStatus = null) {
    let badge = "success";

    if (status == "ACTIVE") {
        badge = "info";
    } else if (status == 'DRAFT') {
        badge = "secondary";
    } else if (status == 'RETURNED') {
        badge = "warning";
    } else if (status == "IN_PROCESS") {
        badge = "blue";
        
        if ($deliveryOrderStatus == 'ATTACHED') {
            badge = 'success';
        }
    } else if (status == "ARRIVED") {
        badge = "purple";
    } else if (status == "CANCELLED") {
        badge = "danger";
    }

    return badge;
}

function getServiceRequestStatusBadge(status) {
    let badge = '<span class="badge bg-label-danger">CANCELLED</span>';

    if (status == "SUBMITTED") {
        badge = '<span class="badge bg-label-progress">SUBMITTED</span>';
    } else if (status == 'MATERIAL_RECEIVED') {
        badge = '<span class="badge bg-label-info">MATERIAL RECEIVED</span>';
    } else if (status == 'IN_PROGRESS') {
        badge = '<span class="badge bg-label-blue">IN PROGRESS</span>';
    } else if (status == "QC") {
        badge = '<span class="badge bg-label-primary">QC</span>';
    } else if (status == "COMPLETED") {
        badge = '<span class="badge bg-label-sucess">COMPLETED</span>';
    } else if (status == "DELIVERED") {
        badge = '<span class="badge bg-label-done">DELIVERED</span>';
    }

    return badge;
}

async function postConfirm({ endpoint, method = 'POST', headers, redirectPath, data }) {
    const confirm = await customSwal({
        title: "Are you sure?",
        text: "Please review all data before proceeding.",
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: "Yes",
        cancelButtonText: "Cancel",
        type: 'confirm',
        showLoaderOnConfirm: true,
    });

    if (confirm.isConfirmed) {
        try {
            const response = await fetchApi({
                endpoint,
                method,
                headers: {
                    'X-CSRF-TOKEN': $('input[name="_token"]').val(),
                    'X-Requested-With': 'XMLHttpRequest',
                    ...headers,
                },
                body: data,
            });
            if (response && response.code == 200) {
                Swal.fire({
                    title: "Success!",
                    text: response.message,
                    icon: "success",
                });
                new Promise((resolve) => {
                    setTimeout(() => {
                        location.href = redirectPath;
                        resolve();
                    }, 1500);
                });
            } else {
                Swal.fire({
                    title: "Error!",
                    text: response.message,
                    icon: "error",
                });
            }
        } catch (error) {
            return Swal.fire({
                title: "Error!",
                text: error.message,
                icon: "error",
            });
        }
    }
}

function toggleSlide(isToggle = true, nodeName = '#slideContainer', action = '') {
    const container = $(nodeName);

    if (isToggle) {
        // Toggle the 'is-open' class to trigger the CSS transition
        return container.toggleClass('is-open');
    }

    if (action == 'add') {
        return container.addClass('is-open');
    }
    
    return container.removeClass('is-open');
}

function getParam(key) {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get(key);
}

function showErrorModal(errorMessage) {
    Swal.fire({
        title: `Error!`,
        text: errorMessage,
        icon: 'error',
        confirmButtonText: 'Dismiss',
        confirmButtonColor: getComputedStyle(document.documentElement).getPropertyValue('--main-color').trim() || '#FB8C00',
    })
}

function onChangeEmployee($select, nodeName) {
    const selectedOption = $select.find('option:selected');
    const employeeName = selectedOption.text();
    $(nodeName).val(employeeName);
}

function convertEstimationTimeToMinutes({ est_start_time, est_end_time, date }) {
    let totalMinutes = dayjs(`${date} ${est_end_time}`).diff(dayjs(`${date} ${est_start_time}`), 'minute');
    
    if (totalMinutes < 0) {
        totalMinutes = dayjs(`${dayjs(date).add(1, 'day').format('YYYY-MM-DD')} ${est_end_time}`).diff(dayjs(`${date} ${est_start_time}`), 'minute');
    }

    return totalMinutes;
}

function colorPicker(selector, defaultColor = '#666CE8') {
    var colorPickerMonolith = document.querySelector(selector);
    var colorPicker = null;
    if (colorPickerMonolith) {
        colorPicker = new Pickr({
            el: colorPickerMonolith,
            theme: "monolith",
            default: defaultColor,
            swatches: [
                "#666CE8",
                "#28D094",
                "#FF4961",
                "#FF9149",
                "#1E9FF2",
            ],
            components: {
                preview: !0,
                opacity: !0,
                hue: !0,
                interaction: {
                    input: !0,
                    clear: !0,
                    save: !0,
                },
            },
        });
    }

    return colorPicker;
}

function initializeColorPicker({ selector, inputSelector, defaultColor = '#666CE8' }) {
    const pickr = colorPicker(selector, defaultColor);
    let isInputUpdating = false;

    pickr.on("save", (color, _) => {
        const hexColor = color.toHEXA().toString();
        const inputHex = $(inputSelector);
        if (!inputHex.is(':focus') && !isInputUpdating) {
            inputHex.val(hexColor);
        }
    });

    const inputHex = $(inputSelector);
    inputHex.on("input change", function() {
        let val = $(this).val().trim();
        if (/^#?([0-9A-F]{3,4}|[0-9A-F]{6}|[0-9A-F]{8})$/i.test(val)) {
            let colorVal = val;
            if (!colorVal.startsWith('#')) {
                colorVal = '#' + colorVal;
            }
            isInputUpdating = true;
            try {
                pickr.setColor(colorVal);
            } catch (e) {
                console.error("Pickr setColor error:", e);
            }
            isInputUpdating = false;
        }
    });
}

function onChangeItemSelection({ $select, unitNodeName = '.units', items }) {
    const itemId = $select.find('option:selected').val();
    const unitAttrName = $select.closest('.row').find(unitNodeName).attr('name');
    const unitSelector = `select[name="${unitAttrName}"]`;
    const selected = items.find(item => item.id == itemId);
    const uoms = [];
    for (const conversion of selected.uom_conversions) {
        uoms.push({
            id: conversion.to_uom.id,
            code: conversion.to_uom.code,
            name: conversion.to_uom.name,
            conversion_rate: Number.parseFloat(conversion.conversion_rate),
        });
    }

    appendUnitSelection({
        nodeName: unitSelector,
        collections: uoms,
    });
}

window.appendTransactionData = appendTransactionData;
window.generateDeliveryOrderPdf = generateDeliveryOrderPdf;
window.appendEmployeeSelection = appendEmployeeSelection;
window.appendBusinessPartnerSelection = appendBusinessPartnerSelection;
window.appendItemSelection = appendItemSelection;
window.appendTransactionSelection = appendTransactionSelection;
window.appendUnitSelection = appendUnitSelection;
window.appendItemTypeSelection = appendItemTypeSelection;
window.appendProductSelection = appendProductSelection;
window.appendCategorySelection = appendCategorySelection;
window.appendAttributeOptionSelection = appendAttributeOptionSelection;
window.appendGaugeSelection = appendGaugeSelection;
window.appendMachineTypeSelection = appendMachineTypeSelection;
window.appendMachineSelection = appendMachineSelection;
window.appendMachineStatusSelection = appendMachineStatusSelection;
window.appendBusinessRoleSelection = appendBusinessRoleSelection;
window.appendDefectTypeSelection = appendDefectTypeSelection;
window.appendMovementTypeSelection = appendMovementTypeSelection;
window.appendModuleSelection = appendModuleSelection;
window.appendUserSelection = appendUserSelection;
window.appendRoleSelection = appendRoleSelection;
window.appendGenderSelection = appendGenderSelection;
window.appendDepartmentSelection = appendDepartmentSelection;
window.appendColorSelection = appendColorSelection;
window.createBusinessPartner = createBusinessPartner;
window.createMachineType = createMachineType;
window.downloadFileFromURL = downloadFileFromURL;
window.browseProducts = browseProducts;
window.getStockLevel = getStockLevel;
window.getStockBadgeClass = getStockBadgeClass;
window.getQualityControlStatusBadge = getQualityControlStatusBadge;
window.getProductionStatusBadge = getProductionStatusBadge;
window.getServiceRequestStatusBadge = getServiceRequestStatusBadge;
window.showModalDeliveryOrder = showModalDeliveryOrder;
window.updateDeliveryOrderStatus = updateDeliveryOrderStatus;
window.showCreatePage = showCreatePage;
window.showEditPage = showEditPage;
window.initializeDropzoneUpload = initializeDropzoneUpload;
window.postConfirm = postConfirm;
window.dayjs = dayjs;
window.getTransactionStatusBadge = getTransactionStatusBadge;
window.initSelect2 = initSelect2;
window.toggleSlide = toggleSlide;
window.getParam = getParam;
window.showErrorModal = showErrorModal;
window.onChangeEmployee = onChangeEmployee;
window.formatMinutesToHumanReadable = formatMinutesToHumanReadable;
window.convertEstimationTimeToMinutes = convertEstimationTimeToMinutes;
window.colorPicker = colorPicker;
window.initializeColorPicker = initializeColorPicker;
window.onChangeItemSelection = onChangeItemSelection;